import { Input, message, Spin, Tabs } from "antd";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TableVirtuoso } from "react-virtuoso";
import apiFactory from "../../../../api";
import "./style.scss";

const { Search } = Input;

const getColumnName = (index) => {
  let name = "";
  let n = index;
  while (n >= 0) {
    name = String.fromCharCode((n % 26) + 65) + name;
    n = Math.floor(n / 26) - 1;
  }
  return name;
};

const TableComponent = React.forwardRef((props, ref) => (
  <table {...props} ref={ref} className="excel-table" />
));

const TableRowComponent = React.forwardRef((props, ref) => (
  <tr {...props} ref={ref} className="excel-row" />
));

const FooterComponent = ({ context }) => {
  if (context?.loadingMore) {
    return (
      <div className="excel-footer-loading">
        <Spin size="small" style={{ marginRight: 8 }} /> Loading more cell
      </div>
    );
  }

  if (!context?.hasMore && context?.dataLength > 0) {
    return <div className="excel-footer-end">--- Show all data ---</div>;
  }

  return null;
};

const virtuosoComponents = {
  Table: TableComponent,
  TableRow: TableRowComponent,
  Footer: FooterComponent,
};
// =====================================================================

const ExcelPreview = ({ fileId }) => {
  const [data, setData] = useState([]);
  const [sheetNames, setSheetNames] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);

  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const skipNextFetch = useRef(false);

  const fetchExcelData = useCallback(
    async (fetchPage, fetchSheet, fetchKeyword, isAppend = false) => {
      if (!fileId) return;

      const controller = new AbortController();
      const payload = {
        limit: 50,
        page: fetchPage,
        search: {
          fileId: fileId,

          sheetName: fetchSheet || null,
          keyword: fetchKeyword || null,
        },
      };

      try {
        if (isAppend) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }

        const response = await apiFactory.previewApi.fetchExcelPreview(
          payload,
          controller.signal,
        );

        const apiBody = response?.data;
        const resData = apiBody?.data || [];
        const resSheetNames = apiBody?.sheets || [];

        const resHasNext = resData.length === payload.limit;

        setSheetNames((prevSheetNames) => {
          if (
            !isAppend &&
            resSheetNames.length > 0 &&
            prevSheetNames.length === 0
          ) {
            if (!fetchSheet) {
              skipNextFetch.current = true;
              setActiveSheet(resSheetNames[0]);
            }
            return resSheetNames;
          }
          return prevSheetNames;
        });

        setData((prevData) => (isAppend ? [...prevData, ...resData] : resData));
        setHasMore(resHasNext);
      } catch (error) {
        if (error.name !== "CanceledError") {
          console.error("Error Excel:", error);
          message.error("Error Excel");
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }

      return () => controller.abort();
    },
    [fileId],
  );

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }

    if (sheetNames.length > 0 && !activeSheet) return;

    setPage(0);
    setData([]);
    setHasMore(true);

    const abortFetch = fetchExcelData(0, activeSheet, keyword, false);
    return () => abortFetch && abortFetch.then((abort) => abort && abort());
  }, [activeSheet, keyword, fetchExcelData]);

  const loadMore = useCallback(() => {
    if (!loading && !loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchExcelData(nextPage, activeSheet, keyword, true);
    }
  }, [
    loading,
    loadingMore,
    hasMore,
    page,
    activeSheet,
    keyword,
    fetchExcelData,
  ]);

  const onSearch = (value) => {
    setKeyword(value);
  };

  const maxCols = useMemo(() => {
    if (!data || data.length === 0) return 0;
    return data.reduce((max, row) => Math.max(max, row ? row.length : 0), 0);
  }, [data]);

  const virtuosoContext = useMemo(
    () => ({
      loadingMore,
      maxCols,
      hasMore,
      dataLength: data.length,
    }),
    [loadingMore, maxCols, hasMore, data.length],
  );

  const excelStyles = `
    .excel-table {
      border-collapse: collapse;
      width: 100%;
      background: white;
      table-layout: fixed;
    }
    .excel-row:hover td:not(.row-header) {
      background-color: #e6f7ff !important;
      filter: brightness(0.95);
    }
  `;

  return (
    <div className="excel-preview-container">
      <div className="excel-header-toolbar">
        <Tabs
          activeKey={activeSheet}
          onChange={(key) => setActiveSheet(key)}
          items={sheetNames.map((sheetName) => ({
            key: sheetName,
            label: sheetName,
          }))}
        />
        <div className="excel-search-wrapper">
          <Search
            placeholder="Search sheet (Enter to find)..."
            onSearch={onSearch}
            onPressEnter={(e) => onSearch(e.target.value)}
            allowClear
            enterButton
          />
        </div>
      </div>

      <div className="excel-table-wrapper">
        {loading ? (
          <div className="excel-loading-overlay">
            <Spin tip="Loading..." size="large" />
          </div>
        ) : data.length > 0 ? (
          <TableVirtuoso
            style={{ height: "100%" }}
            data={data}
            endReached={loadMore}
            overscan={200}
            components={virtuosoComponents}
            context={virtuosoContext}
            fixedHeaderContent={() => (
              <tr>
                <th className="header-index">#</th>
                {Array.from({ length: maxCols }).map((_, i) => (
                  <th key={i}>{getColumnName(i)}</th>
                ))}
              </tr>
            )}
            itemContent={(index, row) => (
              <>
                <td className="row-header">{index + 1}</td>
                {Array.from({ length: maxCols }).map((_, colIndex) => {
                  const cellData = row[colIndex] || {};
                  const cellValue = cellData.value;
                  const cellStyle = cellData.style || {};

                  return (
                    <td
                      key={colIndex}
                      title={cellValue}
                      style={{ ...cellStyle }} // Giữ lại inline style động từ BE
                    >
                      {cellValue !== undefined && cellValue !== null
                        ? String(cellValue)
                        : ""}
                    </td>
                  );
                })}
              </>
            )}
          />
        ) : (
          <div className="excel-empty-state">Empty</div>
        )}
      </div>
    </div>
  );
};

export default ExcelPreview;
